import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, ArrowLeft, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function WarrantyVerification() {
  const { warrantyNumber } = useParams();
  const navigate = useNavigate();
  const [warranty, setWarranty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/warranty/${warrantyNumber}`);
        if (res.ok) {
          setWarranty(await res.json());
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      }
      setLoading(false);
    };
    load();
  }, [warrantyNumber]);

  const isActive = warranty && new Date(warranty.warranty_end_date) > new Date();

  return (
    <div className="min-h-screen bg-stone-50">
      <nav className="bg-white border-b border-stone-200 py-4">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Gravsteinoppfriskeren
          </h1>
          <button onClick={() => navigate('/')} className="text-stone-600 hover:text-stone-900 flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Forsiden
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-800 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-stone-500">Verifiserer garanti...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center" data-testid="warranty-not-found">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-red-600" size={48} />
            </div>
            <h2 className="text-3xl font-bold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Garanti ikke funnet
            </h2>
            <p className="text-stone-600 text-lg mb-2">
              Garantinummer <span className="font-semibold">{warrantyNumber}</span> ble ikke funnet i vårt system.
            </p>
            <p className="text-stone-500 text-sm">
              Kontroller at nummeret er riktig, eller kontakt oss for hjelp.
            </p>
          </div>
        ) : (
          <div data-testid="warranty-verified">
            {/* Status Banner */}
            <div className={`rounded-2xl p-6 mb-6 flex items-center gap-4 ${isActive ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="warranty-status-banner">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                {isActive ? <CheckCircle className="text-emerald-700" size={32} /> : <Clock className="text-amber-700" size={32} />}
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isActive ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {isActive ? 'Gyldig garanti' : 'Garanti utløpt'}
                </h2>
                <p className={`text-sm ${isActive ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {isActive ? 'Denne garantien er verifisert og aktiv.' : 'Garantiperioden har gått ut.'}
                </p>
              </div>
            </div>

            {/* Certificate Card */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-stone-900 p-8 text-center">
                <Shield className="mx-auto text-amber-400 mb-3" size={40} />
                <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Garantibevis
                </h1>
                <p className="text-stone-400">DC-Protect beskyttelse</p>
              </div>

              <div className="p-8 space-y-6">
                {/* Warranty Number */}
                <div className="text-center py-4 bg-emerald-50 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Garantinummer</p>
                  <p className="text-2xl font-bold text-emerald-800" data-testid="verify-warranty-number">{warranty.warranty_number}</p>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-stone-50 p-4 rounded-xl text-center">
                    <p className="text-xs text-stone-400 mb-1">Registrert</p>
                    <p className="text-lg font-semibold text-stone-900">
                      {new Date(warranty.registration_date).toLocaleDateString('no-NO')}
                    </p>
                  </div>
                  <div className="bg-stone-50 p-4 rounded-xl text-center">
                    <p className="text-xs text-stone-400 mb-1">Garanti til</p>
                    <p className={`text-lg font-semibold ${isActive ? 'text-emerald-700' : 'text-red-600'}`}>
                      {new Date(warranty.warranty_end_date).toLocaleDateString('no-NO')}
                    </p>
                  </div>
                </div>

                {/* 10 Year Badge */}
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-full shadow-md">
                    <Shield size={20} />
                    <span className="font-bold text-lg">10 ÅRS GARANTI</span>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="border-t border-stone-100 pt-6 space-y-3">
                  <InfoRow label="Registrert av" value={warranty.customer_name} testid="verify-customer-name" />
                  <InfoRow label="Adresse" value={warranty.address} testid="verify-address" />
                  {warranty.dealer_name && <InfoRow label="Utført av" value={warranty.dealer_name} testid="verify-dealer" />}
                </div>
              </div>

              <div className="bg-stone-50 p-6 text-center border-t border-stone-100">
                <p className="text-sm text-stone-500">Verifisert av Gravsteinoppfriskeren</p>
                <p className="text-xs text-stone-400 mt-1">www.gravsteinoppfriskeren.no | 48 14 77 77</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, testid }) {
  return (
    <div className="flex justify-between items-start" data-testid={testid}>
      <span className="text-sm text-stone-500">{label}</span>
      <span className="text-sm font-medium text-stone-900 text-right">{value}</span>
    </div>
  );
}

export default WarrantyVerification;
